import React, { useState, useEffect, useCallback } from 'react';
import { 
  Calendar,
  Clock,
  Zap,
  RefreshCw,
  Trash2,
  Plus,
  AlertTriangle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { NativeButton, NativeInput, NativeSelect } from '../../components/NativeComponents';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => sessionStorage.getItem('hims_token') || localStorage.getItem('hims_token');

const reservationService = {
  list: async () => {
    const response = await fetch(`${API_URL}/api/reservations`, {
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });
    if (!response.ok) throw new Error('API error');
    return response.json();
  },
  create: async (data) => {
    const response = await fetch(`${API_URL}/api/reservations`, {
      method: 'POST',
      headers: { 
        'Authorization': `Bearer ${getToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.detail || 'Rezervasyon oluşturulamadı');
    return result;
  },
  cancel: async (id) => {
    const response = await fetch(`${API_URL}/api/reservations/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.detail || 'İptal edilemedi');
    return result;
  },
  getStations: async () => {
    const response = await fetch(`${API_URL}/api/resident/charging/stations`, {
      headers: { 'Authorization': `Bearer ${getToken()}` }
    });
    if (!response.ok) throw new Error('API error');
    return response.json();
  }
};

export default function ResidentReservationPage() {
  const [reservations, setReservations] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  
  const [formData, setFormData] = useState({
    station_id: '',
    connector_id: 0,
    date: '',
    time: '',
    duration_minutes: 60
  });

  const fetchData = useCallback(async () => {
    try {
      const [resData, stationData] = await Promise.all([
        reservationService.list(),
        reservationService.getStations()
      ]);
      setReservations(resData.reservations || []);
      setStations(stationData.stations || []);
    } catch (err) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.station_id || !formData.date || !formData.time) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }
    
    setSaving(true);
    try {
      const startTime = new Date(`${formData.date}T${formData.time}`).toISOString();
      await reservationService.create({
        station_id: formData.station_id,
        connector_id: parseInt(formData.connector_id),
        start_time: startTime,
        duration_minutes: parseInt(formData.duration_minutes)
      });
      toast.success('Rezervasyon oluşturuldu!');
      setShowModal(false);
      setFormData({ station_id: '', connector_id: 0, date: '', time: '', duration_minutes: 60 });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (id) => {
    setCancelling(id);
    try {
      await reservationService.cancel(id);
      toast.success('Rezervasyon iptal edildi');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCancelling(null);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('tr-TR', { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">Aktif</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">Tamamlandı</span>;
      case 'cancelled':
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">İptal</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Bugünün tarihi (minimum)
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-6 p-4 max-w-2xl mx-auto" data-testid="resident-reservation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Rezervasyonlar</h1>
          <p className="text-muted-foreground text-sm">Şarj istasyonu rezervasyonlarınız</p>
        </div>
        <NativeButton onClick={() => setShowModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Yeni Rezervasyon
        </NativeButton>
      </div>

      {/* Aktif Rezervasyonlar */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="font-semibold text-foreground flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Rezervasyonlarım
          </h2>
        </div>
        
        {reservations.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Henüz rezervasyonunuz yok</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {reservations.map((res) => (
              <div key={res.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Zap className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{res.station_name}</p>
                    <p className="text-sm text-muted-foreground">
                      Konnektör {(res.connector_id || 0) + 1}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <Clock className="w-4 h-4" />
                    {formatDateTime(res.start_time)}
                  </div>
                  <p className="text-xs text-muted-foreground">{res.duration_minutes} dakika</p>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(res.status)}
                  {res.status === 'active' && (
                    <NativeButton
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCancel(res.id)}
                      disabled={cancelling === res.id}
                    >
                      {cancelling === res.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4 text-red-500" />
                      )}
                    </NativeButton>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Yeni Rezervasyon Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md">
            <div className="px-4 py-3 border-b border-border">
              <h3 className="font-semibold text-foreground">Yeni Rezervasyon</h3>
            </div>
            <form onSubmit={handleCreate} className="p-4 space-y-4">
              <NativeSelect
                label="İstasyon"
                value={formData.station_id}
                onChange={(e) => setFormData({...formData, station_id: e.target.value})}
                required
              >
                <option value="">Seçin...</option>
                {stations.filter(s => s.is_online).map(station => (
                  <option key={station.id} value={station.id}>{station.name}</option>
                ))}
              </NativeSelect>
              
              {formData.station_id && (
                <NativeSelect
                  label="Konnektör"
                  value={formData.connector_id}
                  onChange={(e) => setFormData({...formData, connector_id: e.target.value})}
                >
                  {stations.find(s => s.id === formData.station_id)?.connectors?.map(conn => (
                    <option key={conn.id} value={conn.id}>
                      Konnektör {conn.id + 1} ({conn.status})
                    </option>
                  ))}
                </NativeSelect>
              )}
              
              <NativeInput
                label="Tarih"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                min={today}
                required
              />
              
              <NativeInput
                label="Saat"
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({...formData, time: e.target.value})}
                required
              />
              
              <NativeSelect
                label="Süre"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({...formData, duration_minutes: e.target.value})}
              >
                <option value={30}>30 dakika</option>
                <option value={60}>1 saat</option>
                <option value={90}>1.5 saat</option>
                <option value={120}>2 saat</option>
                <option value={180}>3 saat</option>
              </NativeSelect>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <NativeButton type="button" variant="outline" onClick={() => setShowModal(false)}>
                  İptal
                </NativeButton>
                <NativeButton type="submit" disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Rezervasyon Yap'}
                </NativeButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
